import { useRouter } from 'next/router';
import React from 'react';

import Artifact from 'assets/images/landing/artifact.png';
import Button from 'components/Button/Button';
import { useCopyStore, useGlobalStore } from 'store';
import { ReactComponent as LogoIcon } from 'svgs/logoErrorScreen.svg';
import { isMobile, os } from 'u9/utils/platform';
import { ROUTES } from 'utils/routes';

import * as Styled from './ErrorScreen.styles';

export interface ErrorScreenProps {
  visible?: boolean;
  errorType:
    | '404'
    | 'enableJavaScript'
    | 'unsupportedBrowser'
    | 'windowTooSmall'
    | 'webGL'
    | 'unsupportedWindowAspectRatio';
  handleContinueAnyway?: (state: boolean) => void;
}

const defaultProps: Partial<ErrorScreenProps> = {
  visible: true,
  handleContinueAnyway: null,
};

const ErrorScreen: React.FC<ErrorScreenProps> = ({
  errorType,
  handleContinueAnyway,
}) => {
  const { setError } = useGlobalStore();
  const router = useRouter();
  const {
    copy: { global },
  } = useCopyStore();
  const unsupportedBrowserDescription = isMobile()
    ? global?.errors?.unsupportedBrowser?.body?.mobile
    : os?.name === 'Mac OS'
    ? global?.errors?.unsupportedBrowser?.body?.desktopMac
    : global?.errors?.unsupportedBrowser?.body?.desktopWindows;

  const handle404Click = () => {
    router.push({
      pathname: ROUTES.HOME,
    });
  };

  const handleContinueAnywayClick = () => {
    handleContinueAnyway(errorType !== 'unsupportedWindowAspectRatio');
    setError(false);
  };

  return (
    <Styled.Container
      initial={
        errorType === 'enableJavaScript'
          ? { opacity: 1, pointerEvents: 'auto' }
          : { opacity: 0, pointerEvents: 'none' }
      }
      animate={{ opacity: 1, pointerEvents: 'auto' }}
      transition={{ duration: 0.3 }}
    >
      <Styled.LogoWrapper>
        <LogoIcon tabIndex={0} />
      </Styled.LogoWrapper>
      <Styled.Wrapper>
        <Styled.ImageWrapper
          initial={
            errorType === 'enableJavaScript'
              ? { opacity: 1, x: '0%' }
              : { opacity: 0, x: '-100%' }
          }
          animate={{ opacity: 1, x: '0%' }}
          transition={{ duration: 0.6, delay: 0.2 }}
          isWindowTooSmall={errorType === 'windowTooSmall'}
        >
          <img src={Artifact} alt="artifact" />
        </Styled.ImageWrapper>

        <Styled.ContentWrapper
          isWindowTooSmall={errorType === 'windowTooSmall'}
        >
          <Styled.Title
            initial={
              errorType === 'enableJavaScript'
                ? { opacity: 1, y: '0rem' }
                : { opacity: 0, y: '50rem' }
            }
            animate={{ opacity: 1, y: '0rem' }}
            transition={{ duration: 0.6, delay: 0.8 }}
            isWindowTooSmall={errorType === 'windowTooSmall'}
          >
            <h1
              dangerouslySetInnerHTML={{
                __html:
                  errorType === '404'
                    ? global.errors.notFound.title
                    : errorType === 'enableJavaScript'
                    ? global.errors.javascriptDisabled.title
                    : errorType === 'windowTooSmall'
                    ? global.errors.windowTooSmall.title
                    : errorType === 'webGL'
                    ? global.errors.webGLDisabled.title
                    : errorType === 'unsupportedWindowAspectRatio'
                    ? global.errors.unsupportedWindowAspectRatio.title
                    : global.errors.unsupportedBrowser.title,
              }}
            />
          </Styled.Title>
          <Styled.Description
            initial={
              errorType === 'enableJavaScript'
                ? { opacity: 1, y: '0rem' }
                : { opacity: 0, y: '50rem' }
            }
            animate={{ opacity: 1, y: '0rem' }}
            transition={{ duration: 0.6, delay: 1 }}
            isWindowTooSmall={errorType === 'windowTooSmall'}
          >
            <p
              dangerouslySetInnerHTML={{
                __html:
                  errorType === '404'
                    ? global.errors.notFound.body
                    : errorType === 'enableJavaScript'
                    ? global.errors.javascriptDisabled.body1
                    : errorType === 'windowTooSmall'
                    ? global.errors.windowTooSmall.body
                    : errorType === 'webGL'
                    ? global.errors.webGLDisabled.body
                    : errorType === 'unsupportedWindowAspectRatio'
                    ? global.errors.unsupportedWindowAspectRatio.body
                    : global.errors.unsupportedBrowser.body1,
              }}
            />
          </Styled.Description>
          {errorType === 'unsupportedBrowser' && (
            <>
              <Styled.Browsers
                initial={{ opacity: 0, y: '50rem' }}
                animate={{ opacity: 1, y: '0rem' }}
                transition={{ duration: 0.6, delay: 1.2 }}
              >
                <Styled.Browser>
                  <img src="/images/safari.png" alt="Safari browser logo" />
                  <p>Safari</p>
                </Styled.Browser>
                <Styled.Browser>
                  <img src="/images/chrome.png" alt="Chrome browser logo" />
                  <p>Chrome</p>
                </Styled.Browser>
                <Styled.Browser>
                  <img
                    src="/images/microsoftEdge.png"
                    alt="Microsoft Edge browser logo"
                  />
                  <p>Microsoft Edge</p>
                </Styled.Browser>
                <Styled.Browser>
                  <img src="/images/firefox.png" alt="Firefox browser logo" />
                  <p>Firefox</p>
                </Styled.Browser>
              </Styled.Browsers>

              <Styled.Description
                tabIndex={0}
                initial={{ opacity: 0, y: '50rem' }}
                animate={{ opacity: 1, y: '0rem' }}
                transition={{ duration: 0.6, delay: 1 }}
                isUnsupportedBrowserError={true}
              >
                <p
                  dangerouslySetInnerHTML={{
                    __html: unsupportedBrowserDescription,
                  }}
                />
              </Styled.Description>
            </>
          )}
          {[
            '404',
            'unsupportedBrowser',
            'unsupportedWindowAspectRatio',
          ].includes(errorType) && (
            <>
              <Styled.ButtonWrapper
                initial={{ opacity: 0, y: '50rem' }}
                animate={{ opacity: 1, y: '0rem' }}
                transition={{ duration: 0.6, delay: 1.2 }}
              >
                <Button
                  text={
                    errorType === '404'
                      ? global.errors.notFound.cta
                      : errorType === 'unsupportedWindowAspectRatio'
                      ? global.errors.unsupportedWindowAspectRatio.cta
                      : global.errors.unsupportedBrowser.cta
                  }
                  type="red"
                  onClick={
                    errorType === '404'
                      ? handle404Click
                      : handleContinueAnywayClick
                  }
                />
              </Styled.ButtonWrapper>
            </>
          )}
        </Styled.ContentWrapper>
      </Styled.Wrapper>
    </Styled.Container>
  );
};

ErrorScreen.defaultProps = defaultProps;

export default ErrorScreen;
